import { useState, useEffect } from 'react';

/**
 * useTheme hook - provides theme state and colors
 * Returns theme-aware colors that work in both light and dark mode
 */
const useTheme = () => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('nearU_theme');
    return saved !== 'light';
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('nearU_theme');
      setIsDark(saved !== 'light');
    };

    // Listen for theme changes
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom theme change events
    const handleThemeChange = () => {
      const saved = localStorage.getItem('nearU_theme');
      setIsDark(saved !== 'light');
    };
    window.addEventListener('themeChange', handleThemeChange);

    // Poll for changes (fallback)
    const interval = setInterval(() => {
      const saved = localStorage.getItem('nearU_theme');
      const newIsDark = saved !== 'light';
      if (newIsDark !== isDark) {
        setIsDark(newIsDark);
      }
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('themeChange', handleThemeChange);
      clearInterval(interval);
    };
  }, [isDark]);

  // Theme-aware color palette
  const colors = isDark ? {
    // Backgrounds
    bgPrimary: '#0D0D0D',
    bgSecondary: '#1A0F0A',
    bgCard: '#1C1C1C',
    bgElevated: '#2A1B0E',
    bgInput: '#1C1410',
    bgHover: 'rgba(212, 175, 55, 0.1)',
    bgOverlay: 'rgba(13, 13, 13, 0.98)',
    bgGlass: 'rgba(28, 28, 28, 0.9)',
    
    // Text
    textPrimary: '#F4E7C3',
    textSecondary: '#C8A878',
    textMuted: '#8A7355',
    textGold: '#D4AF37',
    
    // Gold accents
    gold: '#D4AF37',
    goldDark: '#A57C33',
    goldLight: '#D7B56D',
    goldGlow: 'rgba(212, 175, 55, 0.4)',
    
    // Borders
    borderColor: 'rgba(165, 124, 51, 0.4)',
    borderLight: 'rgba(165, 124, 51, 0.2)',
    
    // Shadows
    cardShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
    goldShadow: '0 0 20px rgba(212, 175, 55, 0.3)',
  } : {
    // Light mode - Backgrounds
    bgPrimary: '#FDFCFA',
    bgSecondary: '#F5F2ED',
    bgCard: '#FFFFFF',
    bgElevated: '#FDFCFB',
    bgInput: '#FFFFFF',
    bgHover: 'rgba(212, 175, 55, 0.08)',
    bgOverlay: 'rgba(253, 252, 250, 0.98)',
    bgGlass: 'rgba(255, 255, 255, 0.9)',
    
    // Light mode - Text (high contrast)
    textPrimary: '#1A1410',
    textSecondary: '#4A4035',
    textMuted: '#6D6358',
    textGold: '#8B6914',
    
    // Gold accents (darker for light mode)
    gold: '#8B6914',
    goldDark: '#6B5210',
    goldLight: '#A57C33',
    goldGlow: 'rgba(139, 105, 20, 0.2)',
    
    // Borders (more visible in light mode)
    borderColor: 'rgba(139, 105, 20, 0.25)',
    borderLight: 'rgba(139, 105, 20, 0.15)',
    
    // Shadows
    cardShadow: '0 2px 16px rgba(26, 20, 16, 0.1)',
    goldShadow: '0 0 16px rgba(139, 105, 20, 0.15)',
  };

  return { isDark, colors };
};

export default useTheme;
